import { forwardRef, useEffect, useRef, useState } from 'react';
import TextField, { TextFieldProps } from '../TextField';

export interface UnitTextFieldProps extends Omit<TextFieldProps, 'type'> {
  unit?: string;
  decimals?: number;
  minValue?: number;
  maxValue?: number;
  defaultValue?: string | number;
  forceValue?: boolean;
  allowNegative?: boolean
}

export const UnitTextField = forwardRef<HTMLInputElement, UnitTextFieldProps>(({
  unit = '',
  decimals = 3,
  value = '',
  onChange,
  minValue = 0,
  maxValue = 999_999_999.999,
  defaultValue,
  forceValue = false,
  allowNegative = false,
  ...props
}, ref) => {
  const [internalValue, setInternalValue] = useState<string>(defaultValue ? String(defaultValue) : '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const formatWithThousandSeparator = (number: number) => {
    return number
      .toFixed(decimals)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const formatDefaultValue = (value: string | number) => {
    if (typeof value === 'string') {
      const numericValue = Number(value.replace(/\D/g, '')) / 10 ** decimals;

      return `${formatWithThousandSeparator(numericValue)}${unit ? ` ${unit}` : ''}`;
    }

    if (typeof value === 'number') {
      return `${formatWithThousandSeparator(value)}${unit ? ` ${unit}` : ''}`;
    }

    return `0${decimals > 0 ? ',' : ''}${''.padEnd(decimals, '0')}${unit ? ` ${unit}` : ''}`;
  };

  const handleChange = (e: any) => {
    let inputValue = e.target.value.replace(/[^\d-]/g, '');

    if (unit && e.nativeEvent.inputType === 'deleteContentBackward') {
      inputValue = inputValue.slice(0, -1);
    }

    const sanitizedValue = inputValue.includes('-')
      ? `-${inputValue.replace(/-/g, '')}`
      : inputValue.replace(/-/g, '');

    let numericValue = sanitizedValue === '-' ? 0 : Number(sanitizedValue) / 10 ** decimals

    if (numericValue === 0 && minValue !== 0 && sanitizedValue !== '-') {
      setInternalValue('');

      if (onChange) {
        e.target.value = '';
        onChange(e);
      }

      return;
    }

    if (!allowNegative && numericValue < 0) {
      numericValue = 0
    }

    if (minValue !== undefined && numericValue < minValue) {
      return;
    }

    if (maxValue !== undefined && numericValue > maxValue) {
      return;
    }

    const formattedValue = `${sanitizedValue === '-' ? '-' : formatWithThousandSeparator(numericValue)}${unit ? ` ${unit}` : ''}`;

    setInternalValue(formattedValue);

    if (onChange) {
      e.target.value = formattedValue;
      onChange(e);
    }

    if (inputRef.current) {
      const inputLength = formattedValue.length;
      inputRef.current.setSelectionRange(inputLength, inputLength);
    }
  };

  useEffect(() => {
    if (defaultValue) {
      const formattedValue = formatDefaultValue(defaultValue);
      setInternalValue(formattedValue);
    }
  }, [defaultValue]);

  return (
    <TextField
      ref={(el) => {
        inputRef.current = el;

        if (typeof ref === 'function') {
          return ref(el);
        }

        if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
      }}
      value={forceValue ? value : internalValue || value}
      onChange={handleChange}
      placeholder={`0${decimals > 0 ? ',' : ''}${''.padEnd(decimals, '0')}${unit ? ` ${unit}` : ''}`}
      {...props}
    />
  );
},
);
